import { useMutation } from "react-query";

import { createFinancialGuarantor, updateFinancialGuarantor } from "api/customers";
import { useGlobalToast } from "components/GlobalToastProvider";
/** @typedef {import("api/typesDef").FinancialGuarantor} FinancialGuarantor */

function financialGuarantorAPI(payload) {
  const { isCreating, ...values } = payload;

  if (isCreating) {
    return createFinancialGuarantor(values);
  }

  return updateFinancialGuarantor(values);
}

/**
 * The useUpdateGuarantor hook updates or creates the financial guarantor data for a specific client.
 * @param {Object} options - The options for the hook.
 * @returns {{
 *  updateGuarantorMutation: (payload: FinancialGuarantor & { isCreating: boolean }) => Promise<void>,
 *  isMutationGuarantorIdle: boolean,
 *  isMutationGuarantorLoading: boolean,
 * }} The hook object.
 */
const useUpdateGuarantor = (options = {}) => {
  const { onError = () => {}, ...otherOptions } = options;
  const { showToast } = useGlobalToast();

  const { mutate, isIdle, isLoading, ...rest } = useMutation(
    (payload) => financialGuarantorAPI(payload),
    {
      onError: () => {
        showToast({
          message: "Whoops! Something went wrong while updating the guarantor.",
          errorState: true,
        });
        onError();
      },
      ...otherOptions,
    }
  );

  return {
    updateGuarantorMutation: mutate,
    isMutationGuarantorIdle: isIdle,
    isMutationGuarantorLoading: isLoading,
    ...rest,
  };
};

export default useUpdateGuarantor;
